<script lang="ts">
  /**
   * A light confetti burst for a celebration moment — a new personal record,
   * a match win (`docs/DESIGN.md`'s owner amendment for part 12). Plain DOM
   * + CSS rather than reusing `render/particles.ts`'s canvas pool: that pool
   * is board-cell-unit positioned and lives inside a game screen's own
   * render loop (`docs/decisions/0042`); this fires from a results card that
   * has no canvas of its own to draw into, so a handful of absolutely
   * positioned `<span>`s animated with a CSS keyframe is the simpler
   * mechanism for the same brief, not a second particle engine.
   *
   * `prefers-reduced-motion` is respected for free: `app.css`'s global rule
   * collapses every animation's duration to 0.01ms, which turns the fall
   * into an instant, silent flash rather than a bounce nobody asked to see
   * move — the same degradation every other animated cue in this codebase
   * gets from that one rule.
   */
  let { trigger }: { trigger: number } = $props();

  const COLORS = [
    'var(--ui-accent)',
    'var(--ui-gold)',
    'var(--ui-good)',
    'var(--ui-presence-game)',
    'var(--ui-mark-daily-from)',
  ] as const;

  interface Piece {
    id: number;
    left: number;
    delay: number;
    duration: number;
    color: string;
  }

  let pieces: Piece[] = $state([]);
  let seq = 0;

  $effect(() => {
    if (trigger === 0) return;
    pieces = Array.from({ length: 22 }, () => ({
      id: seq++,
      left: Math.random() * 100,
      delay: Math.random() * 0.15,
      duration: 0.9 + Math.random() * 0.5,
      color: COLORS[Math.floor(Math.random() * COLORS.length)] ?? 'var(--ui-accent)',
    }));
    const timer = setTimeout(() => {
      pieces = [];
    }, 1700);
    return () => {
      clearTimeout(timer);
    };
  });
</script>

{#if pieces.length > 0}
  <div class="confetti" aria-hidden="true">
    {#each pieces as piece (piece.id)}
      <span
        class="piece"
        style="left: {piece.left}%; animation-delay: {piece.delay}s; animation-duration: {piece.duration}s; background: {piece.color};"
      ></span>
    {/each}
  </div>
{/if}

<style>
  .confetti {
    position: fixed;
    inset: 0;
    z-index: 60;
    overflow: hidden;
    pointer-events: none;
  }

  .piece {
    position: absolute;
    top: -5%;
    width: 0.5rem;
    height: 0.9rem;
    opacity: 0.9;
    border-radius: 2px;
    animation-name: confetti-fall;
    animation-timing-function: ease-in;
    animation-fill-mode: forwards;
  }

  @keyframes confetti-fall {
    from {
      transform: translateY(0) rotate(0deg);
      opacity: 1;
    }
    to {
      transform: translateY(110vh) rotate(340deg);
      opacity: 0;
    }
  }
</style>
